import {
    Badge,
    Box,
    FormControl,
    FormControlLabel,
    FormLabel,
    Icon,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import React from 'react';

import {
    ChannelSelector,
    CrisEntityPicker,
    EntityBrandCollectionAc,
    EntityStoreCollectionAc,
    EnumSelector,
    If,
} from 'app/components';
import { PartnerMapOperator } from 'app/enum';
import { Brand, PartnerMapQuery, Store, ValuePair } from 'app/model';
import { EntityChangeEvent, EntityCollectionOnChange, EntityOnChange } from 'app/type';

type CrisValuePair = ValuePair<Brand | undefined, Store | undefined>;

interface PartnerMapFormQueryParams {
    index: number;
    query: PartnerMapQuery;
    onChange: EntityCollectionOnChange<PartnerMapQuery>;
    onRemove: (index: number) => void;
}

const buildRemarks = (query: PartnerMapQuery): string => {
    const remarks: string[] = [];

    if (query.persistentStore) {
        remarks.push(`${query.persistentStore.name} [STORE]`);
    } else if (query.persistentBrand) {
        remarks.push(`${query.persistentBrand.name} [BRAND]`);
    }

    if (query.channel) {
        // remarks.push(`<strong>${query.channel}</strong>`);
        remarks.push(query.channel);
    }

    // remarks.push(`<strong>${query.operator}</strong>`);
    remarks.push(query.operator);
    if (query.modifierBrands.length || query.modifierStores.length) {
        remarks.push(`[${query.modifierType}S]`);
    }
    remarks.push(...query.modifierBrands.map((brand) => brand.name));
    remarks.push(...query.modifierStores.map((store) => store.name));

    return remarks.join(' ');
};

const needModifier = (query: PartnerMapQuery): boolean =>
    query.operator === PartnerMapOperator.INTERSECT || query.operator === PartnerMapOperator.MINUS;

const wrapEntityPicker = (pair: CrisValuePair | undefined, child: React.ReactNode): React.ReactNode => {
    let content: string | undefined;
    let color: 'primary' | 'secondary' = 'primary';

    if (pair?.first) {
        content = 'brand';
    }

    if (pair?.second) {
        content = 'store';
        color = 'secondary';
    }

    return (
        <Badge
            badgeContent={content}
            color={color}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
        >
            {child}
        </Badge>
    );
};

const makeValuePair = (query: PartnerMapQuery): CrisValuePair | undefined => {
    if (!query.persistentBrand && !query.persistentStore) {
        return undefined;
    }

    return { first: query.persistentBrand, second: query.persistentStore };
};

export const PartnerMapFormQuery: React.FunctionComponent<PartnerMapFormQueryParams> = ({
    index,
    query,
    onChange,
    onRemove,
}) => {
    const handleEntityUpdate: EntityOnChange<PartnerMapQuery, unknown> = (evt, name, value) => {
        const change = {
            ...query,
            [name]: value,
            remarks: '',
        };

        if ('modifierType' === name || !needModifier(change)) {
            // clear modifiers when changing type or when they are not needed
            change.modifierBrands = [];
            change.modifierStores = [];
        }

        change.remarks = buildRemarks(change);
        onChange(evt, index, change);
    };

    const handlePersistentUpdate = (evt: EntityChangeEvent, value?: CrisValuePair): void => {
        const change = {
            ...query,
            persistentBrand: value?.first,
            persistentStore: value?.second,
            remarks: '',
        };

        change.remarks = buildRemarks(change);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        onChange(evt, index, change);
    };

    const entityValue = makeValuePair(query);

    return (
        <div>
            <Paper variant='outlined' className='mx-1 my-1'>
                <Box display='flex' flexDirection='column' sx={{ gap: '1em' }} className='mx-1 my-1'>
                    <Box display='flex' flexDirection='row' sx={{ gap: '1em', flexWrap: 'wrap' }}>
                        <Box flexBasis='12rem' mr={2} mb={2}>
                            {wrapEntityPicker(
                                entityValue,
                                <CrisEntityPicker
                                    name='persistentEntity'
                                    value={entityValue}
                                    freeSolo={false}
                                    required
                                    onChange={handlePersistentUpdate}
                                    sx={{ width: '20em' }}
                                    variant='standard'
                                />,
                            )}
                        </Box>
                        <Box flexBasis='12rem' mr={2} mb={2}>
                            <ChannelSelector
                                value={query.channel}
                                label='Channel'
                                onChange={(evt) => handleEntityUpdate(evt, 'channel', evt.target.value)}
                            />
                        </Box>
                        <EnumSelector
                            sx={{ width: '12em' }}
                            label='Operator'
                            theEnum={PartnerMapOperator}
                            value={query}
                            name='operator'
                            onChange={handleEntityUpdate}
                        />
                        <Box flexGrow={1} />
                        <Tooltip title='Remove Union'>
                            <IconButton aria-label='remove union' onClick={() => onRemove(index)} size='large'>
                                <Icon color='error'>delete</Icon>
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <If condition={needModifier(query)}>
                        <Box display='flex' flexDirection='row' sx={{ gap: '1em', flexWrap: 'wrap' }}>
                            <FormControl>
                                <FormLabel id='modifier-radio-group'>Modifier Type</FormLabel>
                                <RadioGroup
                                    row
                                    aria-labelledby='modifier-radio-group'
                                    name='controlled-radio-buttons-group'
                                    value={query.modifierType}
                                    onChange={(evt, value) => handleEntityUpdate(evt, 'modifierType', value)}
                                >
                                    <FormControlLabel value='BRAND' control={<Radio />} label='Brand' />
                                    <FormControlLabel value='STORE' control={<Radio />} label='Store' />
                                </RadioGroup>
                            </FormControl>
                            <Box flexGrow={1}>
                                <If condition={'BRAND' === query.modifierType}>
                                    <EntityBrandCollectionAc
                                        label='Modifier Brands'
                                        name='modifierBrands'
                                        value={query}
                                        onChange={handleEntityUpdate}
                                    />
                                </If>
                                <If condition={'STORE' === query.modifierType}>
                                    <EntityStoreCollectionAc
                                        label='Modifier Stores'
                                        name='modifierStores'
                                        value={query}
                                        onChange={handleEntityUpdate}
                                    />
                                </If>
                            </Box>
                        </Box>
                    </If>
                    <Typography variant='h6'>Remarks</Typography>
                    <Typography>{query.remarks}</Typography>
                </Box>
            </Paper>
            <hr />
        </div>
    );
};
